import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';

interface TaskReassignDialogProps {
  open: boolean;
  taskId: string;
  taskName?: string;
  currentAssignee: string | null;
  candidateGroups: string[];
  onClose: () => void;
  onReassign: (assignee: string) => void;
  isReassigning?: boolean;
}

export function TaskReassignDialog({
  open,
  taskId: _taskId,
  taskName,
  currentAssignee,
  candidateGroups,
  onClose,
  onReassign,
  isReassigning = false,
}: TaskReassignDialogProps) {
  // _taskId is available for future use (e.g., logging, analytics)
  const [assignee, setAssignee] = useState('');
  const [assignmentType, setAssignmentType] = useState<'user' | 'unassign'>('user');

  const handleReassign = useCallback(() => {
    if (assignmentType === 'unassign') {
      onReassign('');
    } else if (assignee.trim()) {
      onReassign(assignee.trim());
    }
  }, [assignee, assignmentType, onReassign]);

  const handleClose = useCallback(() => {
    setAssignee('');
    setAssignmentType('user');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Task neu zuweisen</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {taskName && (
            <Typography variant="body2" color="text.secondary">
              Task: {taskName}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="action" />
            <Typography variant="body2">
              Aktuelle Zuweisung:{' '}
              {currentAssignee ? (
                <Chip label={currentAssignee} size="small" />
              ) : (
                <Chip label="Nicht zugewiesen" size="small" variant="outlined" />
              )}
            </Typography>
          </Box>

          {candidateGroups.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Kandidatengruppen:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {candidateGroups.map((group) => (
                  <Chip key={group} label={group} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel>Aktion</InputLabel>
            <Select
              value={assignmentType}
              label="Aktion"
              onChange={(e) => setAssignmentType(e.target.value as 'user' | 'unassign')}
            >
              <MenuItem value="user">Benutzer zuweisen</MenuItem>
              <MenuItem value="unassign">Zuweisung aufheben</MenuItem>
            </Select>
          </FormControl>

          {assignmentType === 'user' && (
            <TextField
              label="Benutzername"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              size="small"
              fullWidth
              placeholder="z.B. authority"
              helperText="Geben Sie den Benutzernamen ein, dem der Task zugewiesen werden soll"
            />
          )}

          {assignmentType === 'unassign' && currentAssignee && (
            <Alert severity="warning">
              Die Zuweisung von {currentAssignee} wird aufgehoben. Der Task wird dann fuer alle
              Kandidaten verfuegbar sein.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isReassigning}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleReassign}
          disabled={
            isReassigning ||
            (assignmentType === 'user' && !assignee.trim()) ||
            (assignmentType === 'unassign' && !currentAssignee)
          }
        >
          {isReassigning ? 'Wird zugewiesen...' : 'Zuweisen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
